import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  Tooltip,
  TextField
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DownloadIcon from "@mui/icons-material/Download";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import config from "../../../config";
import { normalizeShopifyDomain } from "../../../utils/domainUtils";

const POSConfigure = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Generic states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Store/POS Data
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [posSystems, setPosSystems] = useState([]);
  const [posSystemsLoading, setPosSystemsLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    selectedStoreId: "",
    posSystem: "",
    storeDomain: ""
  });

  const [manualFile, setManualFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Flow State
  const [activeStep, setActiveStep] = useState(0);

  // Mapping State (Step 2)
  const [analyzing, setAnalyzing] = useState(false);
  const [mappingSuggestions, setMappingSuggestions] = useState([]);
  const [targetAttributes, setTargetAttributes] = useState([]);
  const [requiredMissing, setRequiredMissing] = useState([]);
  const [confirmedMappings, setConfirmedMappings] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  const [requiredMissingHints, setRequiredMissingHints] = useState([]);
  const [posIdDupeWarning, setPosIdDupeWarning] = useState(null);
  const [colStats, setColStats] = useState({});
  const [shopifyAutoFields, setShopifyAutoFields] = useState([]); // Fields auto-populated from /products.json

  useEffect(() => {
    loadStores();
    loadPosSystems();
  }, []);

  // --- API Calls (Load Data) ---
  const loadStores = async () => {
    setStoresLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/available-stores?client_id=${config.clientId}`, {
        headers: config.getHeaders()
      });
      if (!response.ok) throw new Error("Failed to load stores");
      const result = await response.json();
      if (result.success && result.data) {
        setStores(result.data);
      }
    } catch (err) {
      console.error(err);
      setError("Could not load available stores.");
    } finally {
      setStoresLoading(false);
    }
  };

  const loadPosSystems = async () => {
    setPosSystemsLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/systems`, {
        headers: config.getHeaders()
      });
      if (!response.ok) throw new Error("Failed to load POS systems");
      const result = await response.json();
      if (result.success && result.data) {
        setPosSystems(result.data);
      }
    } catch (err) {
      setPosSystems([
        { pos_id: "default", pos_name: "Shopify" },
        { pos_id: "dutchie", pos_name: "Dutchie" }
      ]);
    } finally {
      setPosSystemsLoading(false);
    }
  };

  // --- Handlers ---
  const handleInputChange = (field, value) => {
    let updates = { [field]: value };

    // We need to re-evaluate the domain if either store or POS changes
    const currentStoreId = field === "selectedStoreId" ? value : formData.selectedStoreId;
    const currentPos = field === "posSystem" ? value : formData.posSystem;

    if (currentStoreId && currentPos === "Shopify") {
      const selected = stores.find(s => s.client_store_id === currentStoreId);
      let domain = selected?.store_domain || "";

      // Clean domain: remove protocol, www, and any paths/trailing slashes
      if (domain) {
        try {
          // Add dummy protocol if missing so URL parser works
          const urlString = domain.startsWith('http') ? domain : `https://${domain}`;
          const url = new URL(urlString);
          domain = url.hostname.replace(/^www\./, '');
        } catch (e) {
          // Fallback regex if URL parsing fails
          domain = domain.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "").split('/')[0];
        }
      }
      updates.storeDomain = domain;
    } else if (currentPos !== "Shopify") {
      updates.storeDomain = ""; // Clear for non-Shopify
    }

    setFormData(prev => ({ ...prev, ...updates }));
    setError(null);
  };

  // --- Drag & Drop Handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };
  const processFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      setError("Invalid file type. Please upload .xlsx, .xls, or .csv");
      setManualFile(null);
      return;
    }
    setManualFile(file);
    setError(null);
  };

  // --- Analyze File (Step 1 -> 2) ---
  const handleAnalyzeFile = async () => {
    if (!manualFile || !formData.selectedStoreId || !formData.posSystem) return;
    setAnalyzing(true);
    setError(null);

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("file", manualFile);

      const analyzeUrl = `${config.API_BASE_URL}/client/pos/analyze-excel?client_store_id=${formData.selectedStoreId}&pos_system=${formData.posSystem}`;
      const headers = config.getHeaders();
      delete headers['Content-Type'];

      const resp = await fetch(analyzeUrl, { method: "POST", headers, body: formDataPayload });
      const result = await resp.json();

      if (!resp.ok || !result.success) throw new Error(result.message || "Analysis failed");

      const { suggestions, target_attributes, required_missing, required_missing_hints, samples, col_stats, shopify_auto_fields } = result.data;
      setMappingSuggestions(suggestions);
      setConfirmedMappings(suggestions.map(s => ({ ...s })));
      setTargetAttributes(target_attributes || []);
      setRequiredMissing(required_missing || []);
      setRequiredMissingHints(required_missing_hints || []);
      setSampleData(samples || []);
      const statsObj = col_stats || {};
      setColStats(statsObj);
      setShopifyAutoFields(shopify_auto_fields || []);

      const initialPosMapping = suggestions.find(s => s.attribute_name === "pos_product_id");
      if (initialPosMapping) {
        const stats = statsObj[initialPosMapping.excel_col];
        if (stats && stats.uniqueness_pct < 100) {
          setPosIdDupeWarning(`⚠️ Column "${initialPosMapping.excel_col}" is not fully unique (${stats.uniqueness_pct}%).`);
        } else {
          const values = (samples || []).map(row => String(row[initialPosMapping.excel_col] ?? "").trim()).filter(Boolean);
          const hasDupes = values.length > 0 && new Set(values).size < values.length;
          setPosIdDupeWarning(hasDupes ? `⚠️ Column "${initialPosMapping.excel_col}" has duplicate values in samples.` : null);
        }
      } else {
        setPosIdDupeWarning(null);
      }
      setActiveStep(2); // Move to mapping step (index 2 in 3-step flow)
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // --- Mapping Change (Step 2) ---
  const handleExcelMappingChange = (excelCol, newAttributeName) => {
    if (newAttributeName) {
      const alreadyUsed = confirmedMappings.some(m => m.excel_col !== excelCol && m.attribute_name === newAttributeName);
      if (alreadyUsed) {
        setError(`"${newAttributeName}" is already mapped. Bud-i attributes can only be used once.`);
        return;
      }
    }
    setError(null);

    if (newAttributeName === "pos_product_id") {
      const stats = colStats[excelCol];
      if (stats) {
        setPosIdDupeWarning(stats.uniqueness_pct < 100 ? `⚠️ Column "${excelCol}" is not fully unique.` : null);
      } else {
        const values = sampleData.map(row => String(row[excelCol] ?? "").trim()).filter(Boolean);
        setPosIdDupeWarning((values.length > 0 && new Set(values).size < values.length) ? `⚠️ Column "${excelCol}" has duplicate values in samples.` : null);
      }
    } else {
      const wasPosId = confirmedMappings.find(m => m.excel_col === excelCol)?.attribute_name === "pos_product_id";
      if (wasPosId) setPosIdDupeWarning(null);
    }

    const attr = targetAttributes.find(a => a.attribute_name === newAttributeName);
    setConfirmedMappings(prev => prev.map(m =>
      m.excel_col === excelCol
        ? {
          ...m,
          attribute_name: newAttributeName || null,
          product_attribute_master_id: attr?.product_attribute_master_id || null
        }
        : m
    ));
  };

  // --- Final Upload ---
  const handleManualUpload = async () => {
    setLoading(true);
    setError(null);
    setSnackbar({ open: true, message: "Sync process started. This may take a moment...", severity: "info" });

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("file", manualFile);

      if (formData.posSystem === "Shopify") {
        // New Shopify GPT-mapping flow → /upload-shopify-mapped
        if (!formData.storeDomain) throw new Error("Shopify Store Domain is required.");
        formDataPayload.append("confirmed_mappings", JSON.stringify(confirmedMappings));
        const uploadUrl = `${config.API_BASE_URL}/client/pos/upload-shopify-mapped?store_url=${formData.storeDomain}&client_store_id=${formData.selectedStoreId}`;
        const headers = config.getHeaders();
        delete headers['Content-Type'];

        const res = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.message || "Upload failed");

        const { inserted = 0, updated = 0 } = result.data || {};
        setSnackbar({ open: true, message: `✅ Shopify sync complete! Inserted ${inserted}, Updated ${updated}.`, severity: "success" });
      } else {
        // Universal flow for all other POS
        formDataPayload.append("confirmed_mappings", JSON.stringify(confirmedMappings));
        const uploadUrl = `${config.API_BASE_URL}/client/pos/upload-universal?client_store_id=${formData.selectedStoreId}&pos_system=${formData.posSystem}`;
        const headers = config.getHeaders();
        delete headers['Content-Type'];

        const res = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.message || "Upload failed");

        setSnackbar({ open: true, message: `✅ Sync complete! Inserted ${result.data?.inserted || 0}, Updated ${result.data?.updated || 0}.`, severity: "success" });
      }
      setTimeout(() => navigate("/pos-sync"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  // --- UI renderers ---

  const steps = [
    { label: "Select Store & POS", icon: <StorefrontIcon /> },
    { label: "Upload Export", icon: <CloudUploadIcon /> },
    { label: "Review Mapping", icon: <AutoFixHighIcon /> }
  ];

  const handleDownloadTemplate = () => {
    const headers = ["product_name", "product_url", "image_url", "product_type", "product_price", "product_description", "potency", "mood", "product_identifier", "is_available"];
    const row1 = ["Sample Pre-Roll 1", "https://example.com/product-1", "https://example.com/img1.jpg", "Pre-Rolls", "$48.00", "Classic energetic strain", "THC: 30.6%", '"Energetic, Happy"', "sample-preroll-1", "TRUE"];
    const row2 = ["Sample Vaporizer 2", "https://example.com/product-2", "https://example.com/img2.jpg", "Vaporizers", "$65.00", "Ripe fruity flavor", "THC: 85.2%", '"Relaxed, Sleepy"', "sample-vaporizer-2", "TRUE"];
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), row1.join(","), row2.join(",")].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "Bud_i_Product_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: 4 }}>

      {/* Header & Stepper */}
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", mb: 1, letterSpacing: "-0.5px" }}>
          Data Synchronization
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: "auto" }}>
          Seamlessly import your product catalog into Bud-i via Excel or CSV.
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{
          '& .MuiStepConnector-line': { borderColor: theme.palette.divider, borderWidth: 2 },
          '& .Mui-active .MuiStepIcon-root': { color: theme.palette.primary.main },
          '& .Mui-completed .MuiStepIcon-root': { color: theme.palette.success.main }
        }}>
          {steps.map((step, idx) => (
            <Step key={step.label} completed={activeStep > idx}>
              <StepLabel
                StepIconComponent={() => (
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: activeStep > idx ? 'success.main' : activeStep === idx ? 'primary.main' : 'grey.200',
                    color: activeStep >= idx ? 'white' : 'grey.500',
                    boxShadow: activeStep === idx ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
                    transition: 'all 0.3s ease'
                  }}>
                    {activeStep > idx ? <CheckCircleOutlineIcon /> : step.icon}
                  </Box>
                )}
              >
                <Typography sx={{ fontWeight: activeStep === idx ? 700 : 500, mt: 1 }}>{step.label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main Content Area */}
      <Card sx={{
        p: { xs: 3, md: 5 },
        borderRadius: 4,
        boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'visible'
      }}>

        {/* Alerts */}
        <Fade in={!!error}><Box>{error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}</Box></Fade>
        <Fade in={!!success}><Box>{success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}</Box></Fade>

        {/* STEP 0: Selection */}
        {activeStep === 0 && (
          <Fade in={true} timeout={500}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 500, mx: 'auto' }}>
              <FormControl fullWidth>
                <InputLabel>Client Store</InputLabel>
                <Select
                  value={formData.selectedStoreId}
                  onChange={(e) => handleInputChange("selectedStoreId", e.target.value)}
                  label="Client Store"
                  sx={{ borderRadius: 2 }}
                >
                  {storesLoading ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading...</MenuItem> :
                    stores.map(s => <MenuItem key={s.client_store_id} value={s.client_store_id}>{s.store_name}</MenuItem>)
                  }
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>POS System / Source</InputLabel>
                <Select
                  value={formData.posSystem}
                  onChange={(e) => handleInputChange("posSystem", e.target.value)}
                  label="POS System / Source"
                  sx={{ borderRadius: 2 }}
                >
                  {posSystemsLoading ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading...</MenuItem> :
                    posSystems.map(s => <MenuItem key={s.pos_id} value={s.pos_name}>{s.pos_name}</MenuItem>)
                  }
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setActiveStep(1)}
                  disabled={!formData.selectedStoreId || !formData.posSystem}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  Continue to Upload
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {/* STEP 1: Upload Dropzone */}
        {activeStep === 1 && (
          <Fade in={true} timeout={500}>
            <Box>
              {/* Conditional Download Template for non-Shopify */}
              {formData.posSystem !== "Shopify" && (
                <Box sx={{ mb: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1), display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                      Standardized Product Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download our template to ensure your data is formatted correctly before uploading.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    startIcon={<DownloadIcon />}
                    sx={{ flexShrink: 0, borderRadius: 2, fontWeight: 600, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                  >
                    Download Template
                  </Button>
                </Box>
              )}

              {/* Top Summary Bar */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Target Store</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {stores.find(s => s.client_store_id === formData.selectedStoreId)?.store_name || "Unknown"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{formData.posSystem}</Typography>
                </Box>
                <Button size="small" variant="text" onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>Change</Button>
              </Box>

              {formData.posSystem === "Shopify" && (
                <TextField
                  fullWidth
                  label="Shopify Store Domain"
                  value={formData.storeDomain}
                  sx={{ mb: 3 }}
                  InputProps={{ sx: { borderRadius: 2 }, readOnly: true }}
                  helperText="Make sure this is your store's domain. If not, please edit it in Store Management and continue."
                />
              )}

              {/* Dropzone */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.main' : manualFile ? 'success.main' : 'grey.300',
                  bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : manualFile ? alpha(theme.palette.success.main, 0.02) : 'grey.50',
                  borderRadius: 4,
                  p: 6,
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: manualFile ? 'success.dark' : 'primary.main',
                    bgcolor: manualFile ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.primary.main, 0.02)
                  }
                }}
                onClick={() => document.getElementById("file-upload").click()}
              >
                <input
                  type="file"
                  id="file-upload"
                  style={{ display: 'none' }}
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileSelect}
                />

                {manualFile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
                    <Box>
                      <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>{manualFile.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{(manualFile.size / 1024).toFixed(1)} KB</Typography>
                    </Box>
                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500, textDecoration: 'underline' }}>
                      Click or drag to replace
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CloudUploadIcon color={isDragging ? "primary" : "action"} sx={{ fontSize: 60, opacity: 0.8 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Drag & Drop your Export
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        or click to browse files (.xlsx, .csv)
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Action Buttons — identical for Shopify and non-Shopify now */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAnalyzeFile}
                  disabled={!manualFile || analyzing || (formData.posSystem === "Shopify" && !formData.storeDomain)}
                  startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                  sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                >
                  {analyzing ? "AI Analyzing Columns..." : "Analyze & Map Columns"}
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {/* STEP 2: Mapping Review (Only for non-Shopify) */}
        {activeStep === 2 && (
          <Fade in={true} timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Data Mapping</Typography>
                  <Typography variant="body2" color="text.secondary">
                    We've automatically matched your {confirmedMappings.length} columns. Please review required fields.
                  </Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={() => setActiveStep(1)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Back to Upload
                </Button>
              </Box>

              {requiredMissingHints.filter(h => !confirmedMappings.some(m => m.attribute_name === h.attribute)).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  {requiredMissingHints
                    .filter(h => !confirmedMappings.some(m => m.attribute_name === h.attribute))
                    .map(({ attribute, hint }) => (
                      <Alert key={attribute} severity="warning" sx={{ mb: 1, borderRadius: 2, alignItems: 'center' }}>
                        <strong>{attribute}:</strong> {hint}
                      </Alert>
                    ))}
                </Box>
              )}
              {posIdDupeWarning && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, alignItems: 'center' }}>
                  {posIdDupeWarning}
                </Alert>
              )}

              {/* Table */}
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px',
                  bgcolor: 'grey.100', p: 2, borderBottom: '1px solid', borderColor: 'divider'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Excel Column</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Sample Value</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Bud-i Attribute</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', textAlign: 'center' }}>Confidence</Typography>
                </Box>

                <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                  {confirmedMappings.map((mapping, idx) => {
                    const sample = sampleData[0]?.[mapping.excel_col] ?? "—";
                    const conf = mapping.confidence ?? 0;
                    const dotColor = conf >= 0.85 ? '#4caf50' : conf >= 0.6 ? '#ff9800' : '#f44336';

                    return (
                      <Box key={mapping.excel_col} sx={{
                        display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px', gap: 2, alignItems: 'center', p: 2,
                        borderBottom: idx < confirmedMappings.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{mapping.excel_col}</Typography>
                        <Tooltip title={String(sample)}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {String(sample)}
                          </Typography>
                        </Tooltip>

                        <FormControl fullWidth size="small">
                          <Select
                            value={mapping.attribute_name || ""}
                            onChange={(e) => handleExcelMappingChange(mapping.excel_col, e.target.value || null)}
                            displayEmpty
                            disabled={shopifyAutoFields.includes(mapping.attribute_name)}
                            sx={{ borderRadius: 1.5, bgcolor: shopifyAutoFields.includes(mapping.attribute_name) ? 'action.disabledBackground' : 'background.paper', '& .MuiSelect-select': { py: 1 } }}
                          >
                            <MenuItem value=""><Typography variant="body2" color="text.secondary"><em>— Skip / Unmapped —</em></Typography></MenuItem>
                            {targetAttributes
                              .filter(attr => !shopifyAutoFields.includes(attr.attribute_name))
                              .map(attr => {
                                const isUsedElsewhere = confirmedMappings.some(m => m.excel_col !== mapping.excel_col && m.attribute_name === attr.attribute_name);
                                return (
                                  <MenuItem key={attr.product_attribute_master_id} value={attr.attribute_name} disabled={isUsedElsewhere}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', opacity: isUsedElsewhere ? 0.5 : 1 }}>
                                      <Typography variant="body2">{attr.attribute_name} {attr.required && <span style={{ color: theme.palette.error.main }}>*</span>}</Typography>
                                      {isUsedElsewhere && <CheckCircleOutlineIcon fontSize="small" color="action" />}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: mapping.attribute_name ? dotColor : 'grey.300', boxShadow: mapping.attribute_name ? `0 0 8px ${alpha(dotColor, 0.5)}` : 'none' }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: mapping.attribute_name ? 'text.primary' : 'text.disabled' }}>
                            {mapping.attribute_name ? `${Math.round(conf * 100)}%` : "—"}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleManualUpload}
                  disabled={loading || !!posIdDupeWarning || requiredMissing.some(req => !confirmedMappings.find(m => m.attribute_name === req))}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AssignmentTurnedInIcon />}
                  sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                >
                  {loading ? "Processing Sync..." : "Confirm & Start Sync"}
                </Button>
              </Box>
            </Box>
          </Fade>
        )}
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default POSConfigure;
